import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';
import { DeviceViewContent } from 'components/DeviceView/DeviceViewContent/DeviceViewContent';
import {
  MIN_WINDOW_HEIGHT,
  MIN_WINDOW_WIDTH,
} from 'components/DeviceView/deviceViewConstants';
import type {
  DeviceViewEntityId,
  DeviceViewWindow as DeviceViewWindowData,
} from 'components/DeviceView/deviceViewTypes';
import { getContainerDimensions } from 'components/DeviceView/helpers/getContainerDimensions';
import { pipe } from 'fp-ts/lib/function';
import { type FC, memo, useMemo } from 'react';
import { Rnd, type RndDragCallback } from 'react-rnd';
import { generateGuid } from 'utils/guid';
import { useDeviceViewContextSelectors } from '../model/DeviceViewModel';
import { activateWindow } from '../model/commands/activateWindow';
import { updateWindowPosition } from '../model/commands/updateWindowPosition';
import { windowResize } from '../model/commands/windowResize';
import { DeviceViewWindowContent } from './DeviceViewWindowContent';

interface Props extends DeviceViewWindowData {
  active: boolean;
  deviceViewEntityId: DeviceViewEntityId;
}

const BareDeviceViewWindow: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    deviceViewContainer: prop('deviceViewContainer'),
    deviceViewEntities: prop('deviceViewEntities'),
    deviceViewWindows: prop('deviceViewWindows'),
    dispatch: prop('dispatch'),
  });
  const windowGuid = useMemo(() => generateGuid(), []);

  const handleDragStart = useStableCallback(() => {
    ctxProps.dispatch(activateWindow, {
      deviceViewEntityId: props.deviceViewEntityId,
    });
  });

  const handleDragStop: RndDragCallback = useStableCallback((_e, d) => {
    const { height: containerHeight, width: containerWidth } =
      getContainerDimensions(ctxProps.deviceViewContainer);
    const node = d?.node;
    if (node) {
      const rect = node.getBoundingClientRect();
      const windowX = pipe(
        d.x >= containerWidth ? containerWidth - rect.width : d.x,
        (x) => (x < 0 ? 0 : x),
      );
      const windowY = pipe(
        d.y >= containerHeight ? containerHeight - rect.height : d.y,
        (y) => (y < 0 ? 0 : y),
      );
      ctxProps.dispatch(updateWindowPosition, {
        x: windowX,
        y: windowY,
        deviceViewEntityId: props.deviceViewEntityId,
      });
    } else {
      ctxProps.dispatch(updateWindowPosition, {
        x: d.x,
        y: d.y,
        deviceViewEntityId: props.deviceViewEntityId,
      });
    }
  });

  const handleResize = useStableCallback(
    (
      _e: Event,
      _direction: string,
      ref: { offsetWidth: number; offsetHeight: number },
    ) => {
      ctxProps.dispatch(windowResize, {
        width: ref.offsetWidth,
        height: ref.offsetHeight,
        deviceViewEntityId: props.deviceViewEntityId,
      });
    },
  );

  const deviceViewEntity = ctxProps.deviceViewEntities.find(
    (entity) => entity.id === props.deviceViewEntityId,
  );

  if (deviceViewEntity === undefined) {
    return null;
  }

  return (
    <Rnd
      cancel='.window-no-move'
      enableResizing={{
        bottom: true,
        bottomLeft: true,
        bottomRight: true,
        left: true,
        right: true,
        top: true,
        topLeft: true,
        topRight: true,
      }}
      minHeight={MIN_WINDOW_HEIGHT}
      minWidth={MIN_WINDOW_WIDTH}
      onDragStart={handleDragStart}
      onDragStop={handleDragStop}
      onResize={handleResize}
      position={{ x: props.x, y: props.y }}
      size={{ width: props.width, height: props.height }}
      style={{ display: 'block', zIndex: props.zIndex, position: 'fixed' }}
    >
      <DeviceViewWindowContent
        active={props.active}
        content={
          <DeviceViewContent
            activeDeviceViewEntityId={props.deviceViewEntityId}
            hideInactiveEntity
            nameSuffix='window'
          />
        }
        deviceViewEntity={deviceViewEntity}
        deviceViewEntityId={props.deviceViewEntityId}
        guid={windowGuid}
      />
    </Rnd>
  );
};

export const DeviceViewWindow = memo(BareDeviceViewWindow);
